// ===========================================
// Calendar Filters Component
// ===========================================

import {
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { CalendarEventsFilters } from '../../../api/hybridBooking.api';
import { Teacher, Location, Room, Instrument } from './calendar.types';

interface CalendarFiltersProps {
  filters: CalendarEventsFilters;
  teachers: Teacher[];
  locations: Location[];
  rooms: Room[];
  instruments: Instrument[];
  onFilterChange: (field: keyof CalendarEventsFilters) => (event: SelectChangeEvent<string>) => void;
  onLocationChange: (event: SelectChangeEvent<string>) => void;
  onClearFilters: () => void;
  hideTeacherDropdown?: boolean;
}

export default function CalendarFilters({
  filters,
  teachers,
  locations,
  rooms,
  instruments,
  onFilterChange,
  onLocationChange,
  onClearFilters,
  hideTeacherDropdown = false,
}: CalendarFiltersProps) {
  const hasFilters =
    (!hideTeacherDropdown && filters.teacherId) ||
    filters.locationId ||
    filters.roomId ||
    filters.instrumentId;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        {!hideTeacherDropdown && (
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Teacher</InputLabel>
              <Select
                value={filters.teacherId || ''}
                label="Teacher"
                onChange={onFilterChange('teacherId')}
              >
                <MenuItem value="">All Teachers</MenuItem>
                {teachers.map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {teacher.color && (
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: teacher.color,
                            border: '1px solid rgba(0,0,0,0.2)',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {teacher.User.firstName} {teacher.User.lastName}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl size="small" fullWidth>
            <InputLabel>Location</InputLabel>
            <Select
              value={filters.locationId || ''}
              label="Location"
              onChange={onLocationChange}
            >
              <MenuItem value="">All Locations</MenuItem>
              {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl size="small" fullWidth disabled={!filters.locationId}>
            <InputLabel>Room</InputLabel>
            <Select
              value={filters.roomId || ''}
              label="Room"
              onChange={onFilterChange('roomId')}
            >
              <MenuItem value="">All Rooms</MenuItem>
              {rooms.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  {room.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl size="small" fullWidth>
            <InputLabel>Instrument</InputLabel>
            <Select
              value={filters.instrumentId || ''}
              label="Instrument"
              onChange={onFilterChange('instrumentId')}
            >
              <MenuItem value="">All Instruments</MenuItem>
              {instruments.map((instrument) => (
                <MenuItem key={instrument.id} value={instrument.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {instrument.color && (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: instrument.color,
                          border: '1px solid rgba(0,0,0,0.2)',
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {instrument.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClearFilters}
            disabled={!hasFilters}
            fullWidth
          >
            Clear Filters
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
}
